// src/pages/admin/AdminKyc.tsx
import React, { useState, useEffect } from "react";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  User,
  FileText,
  Shield,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  Image as ImageIcon,
  File,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { kycService } from "../../services/api";

interface KycSubmission {
  id: number;
  user_id: number;
  document_type: string;
  status: string;
  admin_notes: string;
  submitted_at: string;
  verified_at?: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    phone?: string;
    verification_level: number;
  };
  document_front_url?: string;
  document_back_url?: string;
  selfie_photo_url?: string;
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

// Modal Component
const KycDetailsModal: React.FC<{
  submission: KycSubmission | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ submission, isOpen, onClose }) => {
  if (!isOpen || !submission) return null;

  const userName = submission.user
    ? `${submission.user.first_name} ${submission.user.last_name}`
    : `User #${submission.user_id}`;
  const userEmail = submission.user?.email || "N/A";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                KYC Submission Details
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                ID: {submission.id} • Submitted:{" "}
                {new Date(submission.created_at).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[70vh]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* User Information */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    User Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-900 dark:text-white">
                        {userName}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                        Email:
                      </span>
                      <span className="text-gray-900 dark:text-white">
                        {userEmail}
                      </span>
                    </div>
                    {submission.user?.phone && (
                      <div className="flex items-center">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                          Phone:
                        </span>
                        <span className="text-gray-900 dark:text-white">
                          {submission.user.phone}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                        Verification Level:
                      </span>
                      <span
                        className={`${GlobalStyles.badge} ${GlobalStyles.badgeInfo}`}
                      >
                        {submission.user?.verification_level || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Document Information */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Document Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <File className="h-4 w-4 text-gray-400 mr-2" />
                      <span className={`${GlobalStyles.badge}`}>
                        {submission.document_type
                          .replace("_", " ")
                          .toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm">
                        Submitted:{" "}
                        {new Date(submission.created_at).toLocaleString()}
                      </span>
                    </div>
                    {submission.verified_at && (
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span className="text-sm">
                          Verified:{" "}
                          {new Date(submission.verified_at).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Status
                  </h4>
                  <div className="flex items-center">
                    {getStatusIcon(submission.status)}
                    <span
                      className={`${GlobalStyles.badge} ml-2 ${getStatusBadge(
                        submission.status
                      )}`}
                    >
                      {submission.status.charAt(0).toUpperCase() +
                        submission.status.slice(1)}
                    </span>
                  </div>
                </div>

                {/* Admin Notes */}
                {submission.admin_notes && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Admin Notes
                    </h4>
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {submission.admin_notes}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Document Images */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Document Images
                </h4>

                {/* Front Document */}
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Document Front
                  </p>
                  {submission.document_front_url ? (
                    <div className="relative group">
                      <img
                        src={submission.document_front_url}
                        alt="Document Front"
                        className="w-full h-48 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/400x300?text=Image+Not+Available";
                        }}
                      />
                      <a
                        href={submission.document_front_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 flex items-center justify-center transition-all rounded-lg"
                      >
                        <span className="opacity-0 group-hover:opacity-100 bg-white dark:bg-gray-800 px-3 py-1 rounded text-sm font-medium transition-opacity">
                          View Full Size
                        </span>
                      </a>
                    </div>
                  ) : (
                    <div className="w-full h-48 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="text-center">
                        <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">
                          Image not available
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Back Document (if exists) */}
                {submission.document_back_url && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      Document Back
                    </p>
                    <div className="relative group">
                      <img
                        src={submission.document_back_url}
                        alt="Document Back"
                        className="w-full h-48 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/400x300?text=Image+Not+Available";
                        }}
                      />
                      <a
                        href={submission.document_back_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 flex items-center justify-center transition-all rounded-lg"
                      >
                        <span className="opacity-0 group-hover:opacity-100 bg-white dark:bg-gray-800 px-3 py-1 rounded text-sm font-medium transition-opacity">
                          View Full Size
                        </span>
                      </a>
                    </div>
                  </div>
                )}

                {/* Selfie Photo */}
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Selfie Photo
                  </p>
                  {submission.selfie_photo_url ? (
                    <div className="relative group">
                      <img
                        src={submission.selfie_photo_url}
                        alt="Selfie"
                        className="w-full h-48 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/400x300?text=Image+Not+Available";
                        }}
                      />
                      <a
                        href={submission.selfie_photo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 flex items-center justify-center transition-all rounded-lg"
                      >
                        <span className="opacity-0 group-hover:opacity-100 bg-white dark:bg-gray-800 px-3 py-1 rounded text-sm font-medium transition-opacity">
                          View Full Size
                        </span>
                      </a>
                    </div>
                  ) : (
                    <div className="w-full h-48 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="text-center">
                        <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">
                          Image not available
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Status Update Modal
const StatusUpdateModal: React.FC<{
  submission: KycSubmission | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (status: string, notes: string) => Promise<void>;
  isLoading: boolean;
}> = ({ submission, isOpen, onClose, onConfirm, isLoading }) => {
  const [status, setStatus] = useState<string>("approved");
  const [notes, setNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !submission) return null;

  const userName = submission.user
    ? `${submission.user.first_name} ${submission.user.last_name}`
    : `User #${submission.user_id}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onConfirm(status, notes);
      setNotes("");
      onClose();
    } catch (error) {
      // Error is handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Update KYC Status
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Updating status for:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {userName}
                </span>
              </p>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Status
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="status"
                    value="approved"
                    checked={status === "approved"}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                    Approve
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="status"
                    value="rejected"
                    checked={status === "rejected"}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-red-500 focus:ring-red-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center">
                    <XCircle className="h-4 w-4 text-red-500 mr-1" />
                    Reject
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Admin Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this KYC decision..."
                rows={3}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.input} w-full`}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                These notes will be visible to the user.
              </p>
            </div>

            {/* Warning for rejection */}
            {status === "rejected" && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    Rejecting will reset the user's verification level to 1.
                    They will need to resubmit KYC.
                  </p>
                </div>
              </div>
            )}

            {/* Success for approval */}
            {status === "approved" && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-green-800 dark:text-green-300">
                    Approving will set the user's verification level to 3,
                    allowing full platform access.
                  </p>
                </div>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${
                status === "approved"
                  ? GlobalStyles.buttonSuccess
                  : GlobalStyles.buttonError
              } ${GlobalStyles.buttonMedium} min-w-24 disabled:opacity-50`}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isSubmitting ? "Updating..." : "Loading..."}
                </div>
              ) : (
                <>
                  {status === "approved" ? (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2" />
                  )}
                  {status === "approved" ? "Approve" : "Reject"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getStatusIcon = (status: string) => {
  switch (status) {
    case "approved":
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case "rejected":
      return <XCircle className="h-4 w-4 text-red-500" />;
    case "pending":
      return <Clock className="h-4 w-4 text-yellow-500" />;
    default:
      return <AlertCircle className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "approved":
      return GlobalStyles.badgeSuccess;
    case "rejected":
      return GlobalStyles.badgeError;
    case "pending":
      return GlobalStyles.badgeWarning;
    default:
      return "";
  }
};

const AdminKyc: React.FC = () => {
  const [kycSubmissions, setKycSubmissions] = useState<KycSubmission[]>([]);
  const [filter, setFilter] = useState({
    status: "",
    document_type: "",
    search: "",
  });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });

  // Modal states
  const [selectedSubmission, setSelectedSubmission] =
    useState<KycSubmission | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string>("");

  useEffect(() => {
    fetchKycSubmissions();
    // eslint-disable-next-line
  }, [filter.status, filter.document_type, pagination.current_page]);

  const fetchKycSubmissions = async () => {
    setLoading(true);
    try {
      const params: any = {
        status: filter.status || undefined,
        document_type: filter.document_type || undefined,
        page: pagination.current_page,
      };

      const response = await kycService.getAllKyc(params);

      // Log the response to debug
      console.log("KYC API Response:", response);

      // Handle the paginated response correctly
      if (response.data && Array.isArray(response.data)) {
        // If response has data array (Laravel pagination)
        setKycSubmissions(response.data);

        if (response.meta) {
          setPagination({
            current_page: response.meta.current_page || 1,
            last_page: response.meta.last_page || 1,
            per_page: response.meta.per_page || 20,
            total: response.meta.total || 0,
          });
        }
      } else if (Array.isArray(response)) {
        // If response is directly an array
        setKycSubmissions(response);
        setPagination({
          current_page: 1,
          last_page: 1,
          per_page: response.length,
          total: response.length,
        });
      } else {
        // Fallback - try to extract data from various possible structures
        const data =
          response.data || response.items || response.submissions || [];
        setKycSubmissions(Array.isArray(data) ? data : []);

        if (response.meta || response.pagination) {
          const meta = response.meta || response.pagination || {};
          setPagination({
            current_page: meta.current_page || 1,
            last_page: meta.last_page || 1,
            per_page: meta.per_page || 20,
            total: meta.total || data.length || 0,
          });
        }
      }

      console.log("KYC Submissions loaded:", kycSubmissions.length);
    } catch (error: any) {
      console.error("Failed to fetch KYC submissions:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to load KYC submissions";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (submission: KycSubmission) => {
    fetchKycDetails(submission.id);
  };

  const fetchKycDetails = async (id: number) => {
    try {
      setUpdatingId(id);
      const response = await kycService.getKycDetails(id);
      setSelectedSubmission(response);
      setDetailsModalOpen(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to load KYC details");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusUpdateClick = (submission: KycSubmission) => {
    setSelectedSubmission(submission);
    setStatusModalOpen(true);
  };

  const handleStatusUpdate = async (status: string, notes: string) => {
    if (!selectedSubmission) return;

    try {
      setUpdatingStatus(status);
      await kycService.updateKycStatus(selectedSubmission.id, {
        status,
        admin_notes: notes || undefined,
      });

      // Update local state
      setKycSubmissions((prev) =>
        prev.map((submission) =>
          submission.id === selectedSubmission.id
            ? {
                ...submission,
                status,
                admin_notes: notes || submission.admin_notes,
                verified_at:
                  status === "approved"
                    ? new Date().toISOString()
                    : submission.verified_at,
                user: submission.user
                  ? {
                      ...submission.user,
                      verification_level: status === "approved" ? 3 : 1,
                    }
                  : submission.user,
              }
            : submission
        )
      );

      toast.success(`KYC ${status} successfully`);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Failed to update KYC status";
      toast.error(errorMessage);
      throw error;
    } finally {
      setUpdatingStatus("");
      setSelectedSubmission(null);
    }
  };

  const handleRefresh = () => {
    fetchKycSubmissions();
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.last_page) {
      setPagination((prev) => ({ ...prev, current_page: page }));
    }
  };

  const handleClearFilters = () => {
    setFilter({
      status: "",
      document_type: "",
      search: "",
    });
    setPagination({
      current_page: 1,
      last_page: pagination.last_page,
      per_page: pagination.per_page,
      total: pagination.total,
    });
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      pagination.current_page - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(
      pagination.last_page,
      startPage + maxVisiblePages - 1
    );

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
            pagination.current_page === i
              ? "bg-primary text-white"
              : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
          }`}
        >
          {i}
        </button>
      );
    }

    return pages;
  };

  const filteredSubmissions = kycSubmissions.filter((submission) => {
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      const userName = submission.user
        ? `${submission.user.first_name || ""} ${
            submission.user.last_name || ""
          }`.toLowerCase()
        : "";
      const userEmail = submission.user?.email?.toLowerCase() || "";
      const userId = submission.user_id.toString();

      return (
        userName.includes(searchLower) ||
        userEmail.includes(searchLower) ||
        userId.includes(searchLower) ||
        submission.document_type.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Calculate statistics
  const pendingCount = kycSubmissions.filter(
    (s) => s.status === "pending"
  ).length;
  const approvedCount = kycSubmissions.filter(
    (s) => s.status === "approved"
  ).length;
  const rejectedCount = kycSubmissions.filter(
    (s) => s.status === "rejected"
  ).length;

  if (loading && kycSubmissions.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
        <span className="ml-3">Loading KYC submissions...</span>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className={GlobalStyles.heading1}>KYC Management</h1>
              <p className={GlobalStyles.bodyText + " mt-2"}>
                Review and manage KYC submissions
              </p>
            </div>
            <div className="flex items-center gap-3">
              {(filter.status || filter.document_type || filter.search) && (
                <button
                  onClick={handleClearFilters}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                >
                  Clear Filters
                </button>
              )}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50`}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div
          className={`${GlobalStyles.gridCols4} gap-6 ${GlobalStyles.sectionSpacing}`}
        >
          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Submissions
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {pagination.total}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20">
                <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Pending
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {pendingCount}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/20">
                <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Approved
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {approvedCount}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Rejected
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {rejectedCount}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/20">
                <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters & Table */}
        <div className={GlobalStyles.card}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 w-full">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, user ID, or document type..."
                  value={filter.search}
                  onChange={(e) =>
                    setFilter({ ...filter, search: e.target.value })
                  }
                  className={`${GlobalStyles.input} pl-10 w-full`}
                />
              </div>
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
                className={GlobalStyles.input}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <select
                value={filter.document_type}
                onChange={(e) =>
                  setFilter({ ...filter, document_type: e.target.value })
                }
                className={GlobalStyles.input}
              >
                <option value="">All Documents</option>
                <option value="passport">Passport</option>
                <option value="id_card">ID Card</option>
                <option value="driver_license">Driver License</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead>
                <tr className={`${GlobalStyles.tableHeader} text-left`}>
                  <th className={GlobalStyles.tableCell}>User</th>
                  <th className={GlobalStyles.tableCell}>Document Type</th>
                  <th className={GlobalStyles.tableCell}>Status</th>
                  <th className={GlobalStyles.tableCell}>Submitted</th>
                  <th className={GlobalStyles.tableCell}>Verified</th>
                  <th className={GlobalStyles.tableCell}>Admin Notes</th>
                  <th className={GlobalStyles.tableCell}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.length > 0 ? (
                  filteredSubmissions.map((submission) => {
                    const userName = submission.user
                      ? `${submission.user.first_name || ""} ${
                          submission.user.last_name || ""
                        }`.trim() || `User #${submission.user_id}`
                      : `User #${submission.user_id}`;
                    const userEmail = submission.user?.email || "N/A";

                    return (
                      <tr key={submission.id} className={GlobalStyles.tableRow}>
                        <td className={GlobalStyles.tableCell}>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mr-3">
                              <User className="h-4 w-4 text-gray-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {userName}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {userEmail}
                              </p>
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                ID: {submission.user_id}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <span
                            className={`text-gray-500 dark:text-gray-400 text-xs`}
                          >
                            {submission.document_type
                              .replace("_", " ")
                              .toUpperCase()}
                          </span>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <div className="flex items-center">
                            {getStatusIcon(submission.status)}
                            <span
                              className={`${
                                GlobalStyles.badge
                              } ml-2 ${getStatusBadge(submission.status)}`}
                            >
                              {submission.status.charAt(0).toUpperCase() +
                                submission.status.slice(1)}
                            </span>
                          </div>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(
                              submission.created_at
                            ).toLocaleDateString()}
                          </span>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(submission.created_at).toLocaleTimeString(
                              [],
                              { hour: "2-digit", minute: "2-digit" }
                            )}
                          </div>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {submission.verified_at
                              ? new Date(
                                  submission.verified_at
                                ).toLocaleDateString()
                              : "—"}
                          </span>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <div
                            className="max-w-xs truncate text-xs text-gray-500 dark:text-gray-400"
                            title={submission.admin_notes || ""}
                          >
                            {submission.admin_notes || "—"}
                          </div>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewDetails(submission)}
                              disabled={updatingId === submission.id}
                              className={`${GlobalStyles.iconButton} text-blue-500 hover:text-blue-600 disabled:opacity-50`}
                              title="View Details"
                            >
                              {updatingId === submission.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>

                            {/* Only show approve/reject buttons for pending submissions */}
                            {submission.status === "pending" && (
                              <>
                                <button
                                  onClick={() =>
                                    handleStatusUpdateClick(submission)
                                  }
                                  disabled={updatingStatus !== ""}
                                  className={`${GlobalStyles.iconButton} text-green-500 hover:text-green-600 disabled:opacity-50`}
                                  title="Approve"
                                >
                                  {updatingStatus === "approved" &&
                                  selectedSubmission?.id === submission.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <CheckCircle className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() =>
                                    handleStatusUpdateClick(submission)
                                  }
                                  disabled={updatingStatus !== ""}
                                  className={`${GlobalStyles.iconButton} text-red-500 hover:text-red-600 disabled:opacity-50`}
                                  title="Reject"
                                >
                                  {updatingStatus === "rejected" &&
                                  selectedSubmission?.id === submission.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <XCircle className="h-4 w-4" />
                                  )}
                                </button>
                              </>
                            )}

                            {/* Show disabled buttons for approved/rejected submissions */}
                            {submission.status !== "pending" && (
                              <div className="flex gap-1">
                                <button
                                  disabled
                                  className={`${GlobalStyles.iconButton} opacity-50 cursor-not-allowed`}
                                  title={`Already ${submission.status}`}
                                >
                                  {submission.status === "approved" ? (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                  )}
                                </button>
                                <span className="text-xs text-gray-500">
                                  {submission.status}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center">
                      <div className="text-center">
                        <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          No KYC submissions found
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          {kycSubmissions.length === 0
                            ? "No KYC submissions have been made yet."
                            : "No submissions match your filters. Try clearing filters."}
                        </p>
                        {kycSubmissions.length === 0 && (
                          <button
                            onClick={handleRefresh}
                            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} mt-4`}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Check Again
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-700 gap-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing{" "}
                <span className="font-medium">
                  {Math.min(
                    (pagination.current_page - 1) * pagination.per_page + 1,
                    pagination.total
                  )}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(
                    pagination.current_page * pagination.per_page,
                    pagination.total
                  )}
                </span>{" "}
                of <span className="font-medium">{pagination.total}</span>{" "}
                submissions
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1 || loading}
                  className={`p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {renderPagination()}

                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={
                    pagination.current_page === pagination.last_page || loading
                  }
                  className={`p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <KycDetailsModal
        submission={selectedSubmission}
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedSubmission(null);
        }}
      />

      <StatusUpdateModal
        submission={selectedSubmission}
        isOpen={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
          setSelectedSubmission(null); // ← Fixed this line
        }}
        onConfirm={handleStatusUpdate}
        isLoading={updatingStatus !== ""}
      />
    </div>
  );
};

export default AdminKyc;
